import {
  Badge,
  Button,
  cn,
  DialogFooter,
  DialogHeader,
  DialogSection,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import {
  Calendar,
  Check,
  CircleX,
  Copy,
  Ellipsis,
  MapPin,
  Trash2,
  User,
  Users,
  X,
  TextAlignStart,
  FileText,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@tellescope/tanstack-query";
import { useEventDetailsDialogContents } from "../../../hooks/use-event-details-dialog-contents";
import { EventMetadataRow } from "./EventMetadataRow";
import { EventMetadataInputRow } from "./EventMetadataInputRow";
import { EventStatusRow } from "./EventStatusRow";
import { DeleteEventWarningPopover } from "./DeleteEventWarningPopover";

export interface EventDetailsDialogContentsProps {
  eventId: string;
  onEnduserClick: (enduserId: string) => void;
  onEditClick: () => void;
  onCopyId?: () => void;
  onDeleteSuccess?: () => void;
  timezone?: string;
}

export const EventDetailsDialogContents = ({
  eventId,
  onEnduserClick,
  onEditClick,
  onCopyId,
  onDeleteSuccess,
  timezone,
}: EventDetailsDialogContentsProps) => {
  const { t } = useTranslation("calendar");
  const { selectedOrganization } = useAuth();

  const {
    event,
    title,
    actionsAreOpen,
    setActionsAreOpen,
    deleteWarningIsOpen,
    setDeleteWarningIsOpen,
    toggleCompleted,
    isCompleted,
    toggleNoShow,
    isNoShow,
    toggleCancelled,
    isCancelled,
    copyEventId,
    deleteEvent,
    deleteEventMutation,
    hasBadges,
    dateString,
    locationStrings,
    userNames,
    patients,
    descriptionText,
    updateEventMutation,
    statusRowCount,
    completedByName,
    lastStatusUpdateName,
    canEdit,
    canDelete,
  } = useEventDetailsDialogContents({
    eventId,
    onCopyId,
    onDeleteSuccess,
    timezone,
  });

  return (
    <>
      <DialogHeader
        data-testid="event-details-dialog"
        className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-0 p-4"
      >
        <div className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: event?.color }} />
        <h2 className="tracking-tight text-base font-semibold leading-snug min-w-0">{title}</h2>
        <Popover open={actionsAreOpen} onOpenChange={(open) => setActionsAreOpen(open)}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm-icon">
              <Ellipsis />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="p-0 w-60">
            <div className="px-3 py-2.5 text-xs font-semibold text-muted-foreground border-b border-b-gray-100">
              <p>{t("Actions")}</p>
            </div>
            {canEdit && (
              <div className="p-1 border-b border-b-gray-100">
                <Button
                  variant="ghost"
                  className="w-full font-normal text-sm text-accent-foreground justify-start h-7"
                  onClick={toggleCompleted}
                >
                  <Check />
                  {isCompleted ? t("Un-mark as Completed") : t("Mark as Completed")}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full font-normal text-sm text-accent-foreground justify-start h-7"
                  onClick={toggleNoShow}
                >
                  <X />
                  {isNoShow ? t("Un-mark as No-Show") : t("Mark as No-Show")}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full font-normal text-sm text-accent-foreground justify-start h-7"
                  onClick={toggleCancelled}
                >
                  <CircleX />
                  {isCancelled ? t("Uncancel Event") : t("Cancel Event")}
                </Button>
              </div>
            )}
            <div className={cn("p-1", isCancelled && "border-b border-b-gray-100")}>
              <Button
                variant="ghost"
                className="w-full font-normal text-sm text-accent-foreground justify-start h-7"
                onClick={copyEventId}
              >
                <Copy />
                {t("Copy Event ID")}
              </Button>
            </div>
            {isCancelled && canDelete && (
              <div className="p-1">
                <DeleteEventWarningPopover
                  open={deleteWarningIsOpen}
                  onOpenChange={setDeleteWarningIsOpen}
                  onConfirm={deleteEvent}
                  isDeleting={deleteEventMutation.isPending}
                >
                  <Button
                    data-testid="event-details-delete-button"
                    variant="ghost"
                    className="w-full font-normal text-sm justify-start h-7 text-destructive focus:text-destructive"
                    onClick={() => setDeleteWarningIsOpen(true)}
                  >
                    <Trash2 />
                    {t("Delete Event")}
                  </Button>
                </DeleteEventWarningPopover>
              </div>
            )}
          </PopoverContent>
        </Popover>
        <div
          className={cn(
            "col-start-2 flex flex-wrap items-center gap-1 transition-all",
            hasBadges ? "h-6 mt-1" : "h-0 mt-0"
          )}
        >
          {!!isCancelled && <Badge className="bg-red-50 text-red-600 rounded-full">{t("Cancelled")}</Badge>}
          {!!isNoShow && (
            <Badge variant="secondary" className="rounded-full">
              {t("No Show")}
            </Badge>
          )}
          {!!isCompleted && (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-full">{t("Completed")}</Badge>
          )}
        </div>
      </DialogHeader>
      <DialogSection className="grid grid-cols-[auto_1fr] items-start gap-3 p-4">
        <EventMetadataRow icon={Calendar} hoverLabel={t("Start and End Date")}>
          {dateString}
        </EventMetadataRow>
        {locationStrings.map((locationName, i) => (
          <EventMetadataRow
            key={locationName}
            icon={MapPin}
            hoverLabel={locationStrings.length > 1 ? t("Location {{number}}", { number: i + 1 }) : t("Location")}
          >
            {locationName}
          </EventMetadataRow>
        ))}
        <EventMetadataRow icon={Users} hoverLabel={t("Users")}>
          {userNames.join(", ")}
        </EventMetadataRow>
        <EventMetadataRow icon={User} hoverLabel={t("Patients")}>
          {!!patients.length && (
            <div className="flex flex-col gap-0.5">
              {patients.map(({ id, name }) => (
                <Button
                  key={id}
                  variant="link"
                  className="text-blue-600 items-start w-fit p-0 h-fit font-light"
                  onClick={() => {
                    onEnduserClick(id);
                  }}
                >
                  {name}
                </Button>
              ))}
            </div>
          )}
        </EventMetadataRow>
        <EventMetadataRow icon={TextAlignStart} hoverLabel={t("Description")} fadedText>
          {descriptionText}
        </EventMetadataRow>
        <EventMetadataInputRow
          icon={FileText}
          hoverLabel={t("Internal notes")}
          value={event?.internalNotes ?? ""}
          onSave={(internalNotes) =>
            updateEventMutation.mutateAsync({
              id: eventId,
              internalNotes,
            })
          }
          placeholder={t("Add internal notes...")}
          textarea
          eventId={eventId}
        />
        {isCancelled && (
          <EventMetadataInputRow
            icon={CircleX}
            value={event?.cancelReason ?? ""}
            onSave={(cancelReason) =>
              updateEventMutation.mutateAsync({
                id: eventId,
                cancelReason,
              })
            }
            placeholder={t("Add cancellation reason...")}
            suggestions={selectedOrganization?.settings?.calendar?.cancelReasons}
            eventId={eventId}
          />
        )}
      </DialogSection>
      {!!statusRowCount && (
        <DialogSection
          className="transition-all p-4 flex flex-col gap-1"
          style={{
            height: statusRowCount * 18 + 32,
          }}
        >
          {isCancelled && <EventStatusRow variant="cancelled" timestamp={event?.cancelledAt || new Date()} />}
          {isNoShow && <EventStatusRow variant="no-show" timestamp={event?.noShowedAt || new Date()} />}
          {isCompleted && (
            <EventStatusRow variant="completed" timestamp={event?.completedAt || new Date()} name={completedByName} />
          )}
          {!!lastStatusUpdateName && <EventStatusRow variant="status-update" name={lastStatusUpdateName} />}
        </DialogSection>
      )}
      {canEdit && (
        <DialogFooter className="p-4 flex gap-2 sm:justify-start justify-start w-full">
          <Button
            data-testid="event-details-edit-button"
            variant="outline"
            onClick={onEditClick}
            className="text-xs font-light py-0 px-1.5 h-[26px]"
          >
            {t("Edit Event")}
          </Button>
        </DialogFooter>
      )}
    </>
  );
};
